import { useQuery } from "@apollo/client/react";
import { Navbar, Page } from "framework7-react";
import { useMemo } from "react";
import { useSwipeable } from "react-swipeable";

import { DimensionLegendBar } from "@/components/DimensionLegendBar";
import { NavLeftPrev } from "@/components/NavLeftPrev";
import { NavRightEmpty } from "@/components/NavRightEmpty";
import { NavRightNext } from "@/components/NavRightNext";
import { NavTitle } from "@/components/NavTitle";
import { ReviewButtonBar } from "@/components/ReviewButtonBar";
import { WeeklyBarChart } from "@/components/WeeklyBarChart";
import { WeeklyReviewPageTitle } from "@/components/WeeklyReviewPageTitle";
import { formatISODateString } from "@/config/date-fns";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { getFragmentData } from "@/generated/graphql";
import { EntryFragmentDoc, GetWeeklyDataDocument } from "@/generated/graphql/graphql";
import { usePageReady } from "@/hooks/usePageReady";
import { useCalendarStore } from "@/stores/calendarStore";

export const WeeklyReviewPage = createPage("WeeklyReviewPage", PageComponent);

function PageComponent({ pageName }: PageComponentProps) {
  const { isPageReady, getPageProps } = usePageReady(pageName);
  const { weekly } = useCalendarStore();

  const dateStr = useMemo(() => formatISODateString(weekly.weekStart), [weekly.weekStart]);

  const { data, refetch } = useQuery(GetWeeklyDataDocument, {
    skip: !isPageReady,
    variables: { date: dateStr },
  });
  const entries = getFragmentData(EntryFragmentDoc, data?.weeklyEntries) ?? [];

  const swipeHandlers = useSwipeable({
    onSwipedLeft: weekly.next,
    onSwipedRight: weekly.prev,
  });

  return (
    <Page
      ptr
      ptrMousewheel
      data-component={pageName}
      onPtrRefresh={(done) => {
        refetch().then(done);
      }}
      {...getPageProps()}
    >
      <Navbar>
        <NavLeftPrev onClick={weekly.prev} />
        <NavTitle>
          <WeeklyReviewPageTitle />
        </NavTitle>
        {weekly.isCurrentWeek ? <NavRightEmpty /> : <NavRightNext onClick={weekly.next} />}
      </Navbar>

      <div {...swipeHandlers}>
        <WeeklyBarChart
          baseDate={weekly.weekEnd}
          entries={entries}
        />
      </div>

      <DimensionLegendBar />

      <ReviewButtonBar activeButton="weekly" />
    </Page>
  );
}
